import React, { useContext, useEffect, useState } from 'react';
import { View } from 'react-native';
import { useMutation } from '@apollo/client';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import DocsCard from 'app/src/components/Docs/Card';
import TwoFactorDoc from 'app/src/components/Docs/Account/TwoFactor';
import ClientContext from 'app/src/contexts/ClientContext';
import { Fields, defaultValues, PHONE_PATTERN } from 'app/src/elements/forms';
import { Button } from 'app/src/elements/buttons';
import { Loading } from 'app/src/elements/DataState';
import { Notice } from 'app/src/elements/Notice';
import { Title, Text } from 'app/src/styles';

import { UPDATE_USER_OTP_TYPE } from 'app/src/components/Users/queries';

import SetupTOTP from './SetupTOTP';

export const OTP_TYPES = [
  { value: 'email', labelTranslation: 'options.otpType.email' },
  { value: 'sms', labelTranslation: 'options.otpType.sms' },
  { value: 'call', labelTranslation: 'options.otpType.call' },
  { value: 'app', labelTranslation: 'options.otpType.app' },
];

const FIELDS = {
  otpType: {
    select: OTP_TYPES,
    rules: { required: true },
  },
  otpPhoneNumber: {
    hintTranslation: 'hints.user.otpPhoneNumber',
    rules: {
      pattern: {
        value: PHONE_PATTERN,
        message: 'errors.phoneNumberFormat',
      },
    },
  },
  emailEnabled: {
    labelTranslation: 'labels.otpEmailEnabled',
    type: 'hidden',
  },
};

const TwoFactor = ({ user, refetch, hideAppSetup }) => {
  const { currentUser, refetchCurrentUser } = useContext(ClientContext);
  const [formStatus, setFormStatus] = useState();
  const { t } = useTranslation();

  if (!user) {
    user = currentUser;
    refetch = refetchCurrentUser;
  }

  const {
    control, handleSubmit, formState: { errors }, reset, watch
  } = useForm({
    defaultValues: defaultValues({ fields: FIELDS, resource: user })
  });

  const [updateUserOtpType] = useMutation(UPDATE_USER_OTP_TYPE, {
    onCompleted: () => {
      setFormStatus('submitted');
      refetch();
    }
  });

  // Updates default OTP type when app authentication setup.
  useEffect(() => {
    reset({
      otpType: user.otpType,
      otpPhoneNumber: user.otpPhoneNumber,
      emailEnabled: user.otpTypesEnabled.includes('email'),
    });
  }, [user?.otpType]);

  if (!user) { return <Loading />; }

  const enableEmailOtp = () => {
    updateUserOtpType({
      variables: {
        id: user.id, otpType: 'email', otpTypesEnabled: ['email']
      }
    });
  };

  const onSubmit = ({ emailEnabled, ...input }) => {
    setFormStatus('submitting');

    const otpTypesEnabled = [input.otpType];

    if (emailEnabled) {
      otpTypesEnabled.push('email');
    }

    if (input.otpPhoneNumber) {
      otpTypesEnabled.push('call');
      otpTypesEnabled.push('sms');
    }

    if (user.otpTypesEnabled.includes('app')) {
      otpTypesEnabled.push('app');
    }

    input.otpTypesEnabled = [...new Set(otpTypesEnabled)];
    input.otpTypesEnabled.sort();

    updateUserOtpType({ variables: { ...input, id: user.id } });
  };

  const otpType = watch('otpType');
  const fields = { ...FIELDS };

  fields.otpPhoneNumber = {
    ...fields.otpPhoneNumber,
    rules: {
      ...fields.otpPhoneNumber.rules,
      required: ['sms', 'call'].includes(otpType)
    },
  };

  fields.emailEnabled = {
    ...fields.emailEnabled,
    type: ('email' === otpType ? 'hidden' : 'boolean'),
  };

  const otpTypeOptions = user.otpTypesEnabled.includes('app') ?
    OTP_TYPES : OTP_TYPES.filter(type => 'app' !== type.value);

  fields.otpType = { ...fields.otpType, select: otpTypeOptions };

  if ('none' === user.otpType) {
    return (
      <View>
        <Title>{t('titles.twoFactorDisabled')}</Title>
        <Text>{t('notices.twoFactorDisabled')}</Text>
        <Button
          label={t('actions.enableTwoFactor')}
          onPress={enableEmailOtp}
          wide
        />
      </View>
    );
  }

  return (
    <View style={{ maxWidth: 1000 }}>
      <View style={{ flexDirection: 'row', justifyContent: 'space-between' }}>
        <Title style={{ flex: 1 }}>{t('titles.twoFactor')}</Title>
        <DocsCard title={t('titles.twoFactor')} element={TwoFactorDoc} />
      </View>

      {'submitted' === formStatus && (
        <Notice type="success">{t('notices.settingsUpdated')}</Notice>
      )}

      <Fields fields={fields} control={control} errors={errors} />

      <Button label={t('actions.save')} onPress={handleSubmit(onSubmit)} wide />

      {!hideAppSetup && (
        <View style={{ marginTop: 50 }}>
          <SetupTOTP />
        </View>
      )}
    </View>
  );
};

export default TwoFactor;
